// frontend/src/utils/categoryPickerColDef.js
//
// ONE grid cell for "the item's category", whatever that means on this store,
// and ONE place that turns what the merchant picked into a write.
//
// The store's capability (`useCategoryEditing()` → core.category_editing on the
// backend) says which field carries the write, whether a value may be minted,
// and how far one write reaches. This module renders that; call sites do not
// ask which mode the store is on.
//
// The pick becomes a payload through `categoryWrite` in utils/categoryEditing —
// the pure half of the same rule, kept separate so the Data Health resolution
// logic can read it without pulling a grid editor into its unit tests.
//
// TWO CELL BEHAVIOURS, ONE DOOR. They differ because the domain does, and the
// capability is what states the difference:
//
//   product_category_id — one node of Shopify's ~10K-leaf taxonomy, assigned
//                         per item. Closed set, so the cell value is the
//                         option object and the clipboard carries its id
//                         (display_label is deliberately non-unique — the
//                         picker shows the parent tail to disambiguate — so a
//                         label cannot be reversed to one category).
//   item_type           — Shopify's merchant-defined product type, which IS
//                         the category on a PRODUCT_TYPE store. Open set, so
//                         the list carries a "create" row for whatever was
//                         typed, the cell value is the plain trimmed string,
//                         and the clipboard carries it verbatim.
//
// Both read the SAME endpoint (/api/product-categories/, mode-aware
// server-side) through hooks/useProductCategorySearch, which owns fetching and
// caching. The column mechanics for the closed-set case stay in
// referencePickerColDef — the shared Rich Select every dropdown cell uses.
//
// Consumers: ItemSetup (real fields `product_category` / `item_type`) and
// DataHealth's missing-category fix (synthetic `_new_category` /
// `_new_item_type`).

import {
  fetchProductCategoriesPage, fetchProductCategoryPage,
  fetchProductCategoryWindow, PAGE_SIZE, STALE_MS,
} from '../hooks/useProductCategorySearch';
import { productCategories as productCategoriesKey } from '../queries/keys';
import { referencePickerColDef, rememberOption } from './referencePickerColDef';

const EM_DASH = '—';

/**
 * Warm the first page (empty search) into cache so the picker opens already
 * populated. Call from a grid that hosts a category cell, before edit starts —
 * both capabilities read the same (storeId, term, page) key.
 * @returns {Promise<void>}
 */
export function prefetchCategoryOptionsFirstPage(queryClient, storeId) {
  return queryClient.prefetchQuery({
    queryKey: productCategoriesKey.searchPage(storeId, '', 1),
    queryFn: () => fetchProductCategoriesPage('', 1, PAGE_SIZE),
    staleTime: STALE_MS,
  });
}

// ── Closed set: the Shopify taxonomy ────────────────────────────────────────
// Server-paged typeahead over ~10K leaves. `lastRow` = total match count, so
// the Rich Select knows when it has reached the end; every paged option is
// memoised so a later paste of its id can rebuild the object.
function taxonomyColDef({ field, queryClient, storeId, placeholder }) {
  return referencePickerColDef({
    field,
    asyncSource: {
      optionId: 'id',
      formatLabel: (cat) => cat?.display_label || '',
      placeholder,
      valuesPageSize: PAGE_SIZE,
      valuesPage: async ({ search, startRow, endRow }) => {
        const term = (search ?? '').trim();
        const { values, count } = await fetchProductCategoryWindow(
          queryClient, storeId, term, startRow, endRow,
        );
        values.forEach((v) => rememberOption(v, 'id'));
        return { values, lastRow: count, cursor: null };
      },
    },
  });
}

// ── Open set: the store's own product types ─────────────────────────────────
// The editor's list values are `{name, isNew}` objects so the create row can be
// labelled apart from the types that exist; `parseValue` flattens the pick back
// to the string the row holds. Both shapes reach `formatValue` (AG Grid renders
// the current cell value through it when the editor opens), so it reads either.
function optionLabel(value, createLabel) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return value.isNew ? createLabel(value.name) : value.name;
}

function productTypeColDef({
  field, creatable, queryClient, storeId, placeholder, createLabel,
}) {
  // Does the store already use this exact type? Read from page 1 of the same
  // search the list is showing, which the window fetch has already put in the
  // TanStack cache — a cache hit, not a second round-trip. Casefolded, because
  // that is how the backend keys the derived node.
  const alreadyExists = async (term) => {
    const { values } = await fetchProductCategoryPage(queryClient, storeId, term, 1);
    const wanted = term.toLowerCase();
    return values.some((v) => String(v.display_label ?? '').toLowerCase() === wanted);
  };

  return {
    field,
    // AG Grid's type inference would coerce the Rich Select's object commit to
    // a string before parseValue runs, losing `isNew`.
    cellDataType: false,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorParams: {
      allowTyping: true,
      filterList: true,
      filterListAsync: true,
      searchDebounceDelay: 250,
      valuesPageSize: PAGE_SIZE,
      valuesPageLoadThreshold: 8,
      valuePlaceholder: placeholder,
      formatValue: (value) => optionLabel(value, createLabel),
      // Rich Select → row: the picked option is an object, the row wants the
      // bare type. Trimmed so the value the merchant sees is the value pushed,
      // and so two visually identical types can't derive two category nodes.
      parseValue: (value) => {
        const name = typeof value === 'string' ? value : value?.name;
        return String(name ?? '').trim() || null;
      },
      valuesPage: async ({ search, startRow, endRow }) => {
        const term = (search ?? '').trim();
        const { values, count } = await fetchProductCategoryWindow(
          queryClient, storeId, term, startRow, endRow,
        );
        const options = values.map((v) => ({ name: v.display_label }));
        // `creatable` is the STORE's answer, not this cell's. They agree today
        // (a product type is the merchant's own string), and reading it here
        // rather than assuming it is what keeps them from drifting apart: a
        // capability that says a type may not be minted must not get an
        // affordance to mint one.
        if (!creatable || !term || await alreadyExists(term)) {
          return { values: options, lastRow: count, cursor: null };
        }
        // The typed type is new — offer it as the LAST row of the virtual list
        // (index `count`), so the real matches keep their server row indices
        // and the paging arithmetic stays untouched.
        if (endRow > count) options.push({ name: term, isNew: true });
        return { values: options, lastRow: count + 1, cursor: null };
      },
    },
    // Clipboard / fill / CSV carry the type verbatim — it IS the value, and it
    // is unique per store (the derived node's PK is its casefolded form).
    valueFormatter: (params) => params.value ?? '',
    // Reverse: a pasted string (an editor commit is already flattened to one by
    // parseValue). Trimmed so paste obeys the same rule as the picker, and the
    // em dash — what the renderer draws over an empty cell — clears the type
    // rather than becoming one.
    valueParser: (params) => {
      const nv = params.newValue;
      if (nv == null || nv === EM_DASH) return null;
      return String(nv).trim() || null;
    },
  };
}

/**
 * Build the category cell for this store. The caller spreads the result and
 * adds page-specific keys (headerName, width, editable, cellRenderer,
 * cellClassRules).
 *
 * @param {object} opts
 * @param {{write_field: string, creatable: boolean}} opts.capability
 *   From `useCategoryEditing()`.
 * @param {string} opts.field  Column field — also the rowData key.
 * @param {import('@tanstack/react-query').QueryClient} opts.queryClient
 * @param {string} opts.storeId  Cache segment (option rows are per-store).
 * @param {string} opts.placeholder  Typeahead input placeholder (i18n'd).
 * @param {(name: string) => string} [opts.createLabel]  Labels the create row,
 *   e.g. `Create “Winter coats”`. Required when `capability.creatable`.
 * @returns {object} Partial column def.
 */
export function categoryPickerColDef({
  capability, field, queryClient, storeId, placeholder, createLabel,
}) {
  if (!field || !queryClient || !capability?.write_field) {
    throw new Error('categoryPickerColDef: capability, field, and queryClient are required');
  }
  if (capability.creatable && typeof createLabel !== 'function') {
    throw new Error(
      `categoryPickerColDef: ${capability.write_field} is creatable on this `
      + 'store, so createLabel is required to label the create row',
    );
  }
  if (capability.write_field === 'item_type') {
    return productTypeColDef({
      field, creatable: capability.creatable, queryClient, storeId,
      placeholder, createLabel,
    });
  }
  // A closed set cannot mint a member: there is no client-side act that makes
  // a Shopify taxonomy node exist. A capability asking for one is a backend
  // table this cell cannot honour, so it fails here rather than dropping the
  // affordance quietly — the merchant would be told a value can be created
  // and find no way to create it.
  if (capability.creatable) {
    throw new Error(
      `categoryPickerColDef: ${capability.write_field} is a closed option set, `
      + 'so it cannot be creatable — fix core.category_editing',
    );
  }
  return taxonomyColDef({ field, queryClient, storeId, placeholder });
}
